/**
 * PageNotFound - Light Theme 404 Page
 * 
 * Eye-friendly design with:
 * - Clean white backgrounds
 * - Soft animations
 * - Muted accent colors
 */

import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { keyframes } from '@emotion/react';
import { useNavigate } from 'react-router-dom';
import Cookies from 'universal-cookie';
import { useTranslation } from 'react-i18next';
import HomeIcon from '@mui/icons-material/Home';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import '../styles/ModernStyles.css';

const cookies = new Cookies();

// Animation keyframes
const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0) rotate(0deg); }
  50% { transform: translateY(-12px) rotate(3deg); }
`;

const orbit = keyframes`
  0% { transform: rotate(0deg) translateX(60px) rotate(0deg); }
  100% { transform: rotate(360deg) translateX(60px) rotate(-360deg); }
`;

const glitch = keyframes`
  0%, 100% { 
    clip-path: inset(0 0 0 0);
    transform: translate(0);
  }
  5% { 
    clip-path: inset(20% 0 40% 0);
    transform: translate(-3px, 2px);
  }
  10% { 
    clip-path: inset(60% 0 10% 0);
    transform: translate(3px, -2px);
  }
  15% { 
    clip-path: inset(0 0 0 0);
    transform: translate(0);
  }
`;

// Floating Shape Component - Light Theme
const FloatingShape = ({ size, top, left, right, bottom, delay, filled = false }) => (
  <Box
    sx={{
      position: 'absolute',
      top,
      left,
      right,
      bottom,
      width: size,
      height: size,
      borderRadius: filled ? '12px' : '50%',
      border: filled ? 'none' : '2px solid rgba(78, 205, 196, 0.3)',
      background: filled ? 'rgba(78, 205, 196, 0.05)' : 'transparent',
      opacity: 0.6,
      animation: `${float} ${6 + delay}s ease-in-out infinite`,
      animationDelay: `${delay}s`,
      transform: filled ? 'rotate(15deg)' : 'none',
      pointerEvents: 'none',
    }}
  />
);

// Orbiting Element
const OrbitingElement = ({ delay, size, color }) => (
  <Box
    sx={{
      position: 'absolute',
      width: size,
      height: size,
      borderRadius: '6px',
      background: `linear-gradient(135deg, ${color} 0%, transparent 100%)`,
      animation: `${orbit} ${8 + delay}s linear infinite`,
      animationDelay: `${delay}s`,
      opacity: 0.5,
    }}
  />
);

export default function PageNotFound() {
  const [t] = useTranslation('common');
  const navigate = useNavigate();

  const handleGoHome = () => {
    const hostUrl = cookies.get('hostUrl');
    if (hostUrl) {
      window.location.href = hostUrl;
    } else {
      navigate('/');
    }
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #F8FAFC 0%, #EEF2F7 50%, #E2E8F0 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Floating Background Shapes */}
      <FloatingShape size={100} top="10%" left="10%" delay={0} />
      <FloatingShape size={60} top="20%" right="15%" delay={1.5} />
      <FloatingShape size={80} bottom="15%" left="8%" delay={2} />
      <FloatingShape size={50} bottom="20%" right="10%" delay={0.5} />
      <FloatingShape size={70} top="50%" left="5%" delay={1} filled />

      {/* Main Card */}
      <Box
        sx={{
          width: '90%',
          maxWidth: 480,
          background: '#FFFFFF',
          borderRadius: 4,
          padding: 5,
          border: '1px solid #E2E8F0',
          boxShadow: '0 20px 50px rgba(0, 0, 0, 0.08)',
          position: 'relative',
          zIndex: 1,
          animation: `${fadeInUp} 0.8s ease-out forwards`,
          textAlign: 'center',
        }}
      >
        {/* 404 with orbiting elements */}
        <Box
          sx={{
            position: 'relative',
            width: 180,
            height: 120,
            margin: '0 auto 24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {/* Orbiting elements */}
          <OrbitingElement delay={0} size={10} color="#4ECDC4" />
          <OrbitingElement delay={2} size={8} color="#6C9BCF" />
          <OrbitingElement delay={4} size={12} color="#10B981" />

          {/* 404 Text */}
          <Typography
            sx={{
              fontSize: 80,
              fontWeight: 900,
              background: 'linear-gradient(135deg, #4ECDC4 0%, #6C9BCF 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              fontFamily: 'Cairo, sans-serif',
              animation: `${glitch} 4s ease-in-out infinite`,
              letterSpacing: '-4px',
            }}
          >
            404
          </Typography>
        </Box>

        {/* Title */}
        <Typography
          sx={{
            fontSize: 24,
            fontWeight: 700,
            color: '#1E293B',
            fontFamily: 'Cairo, sans-serif',
            marginBottom: 1.5,
          }}
        >
          {t('GLOBAL.pageNotFound') || 'Page Not Found'}
        </Typography>

        {/* Message */}
        <Typography
          sx={{
            fontSize: 15,
            color: '#64748B',
            fontFamily: 'Cairo, sans-serif',
            marginBottom: 3,
            lineHeight: 1.7,
          }}
        >
          {t('GLOBAL.pageNotFoundMessage') || 'The page you\'re looking for doesn\'t exist or has been moved.'}
        </Typography>

        {/* Action Buttons */}
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            justifyContent: 'center',
            flexWrap: 'wrap',
          }}
        >
          <Button
            onClick={handleGoBack}
            startIcon={<ArrowBackIcon />}
            sx={{
              background: '#F8FAFC',
              color: '#64748B',
              padding: '12px 24px',
              borderRadius: 2,
              fontFamily: 'Cairo, sans-serif',
              fontWeight: 600,
              fontSize: 14,
              textTransform: 'none',
              border: '1px solid #E2E8F0',
              transition: 'all 0.3s ease',
              '&:hover': {
                background: '#EEF2F7',
                borderColor: '#4ECDC4',
                color: '#4ECDC4',
              },
            }}
          >
            {t('GLOBAL.goBack') || 'Go Back'}
          </Button>
          <Button
            onClick={handleGoHome}
            startIcon={<HomeIcon />}
            sx={{
              background: 'linear-gradient(135deg, #4ECDC4 0%, #6C9BCF 100%)',
              color: '#fff',
              padding: '12px 24px',
              borderRadius: 2,
              fontFamily: 'Cairo, sans-serif',
              fontWeight: 600,
              fontSize: 14,
              textTransform: 'none',
              boxShadow: '0 4px 15px rgba(78, 205, 196, 0.25)',
              transition: 'all 0.3s ease',
              '&:hover': {
                background: 'linear-gradient(135deg, #3DBCB4 0%, #5B8ABE 100%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 25px rgba(78, 205, 196, 0.35)',
              },
            }}
          >
            {t('GLOBAL.goHome') || 'Go Home'}
          </Button>
        </Box>

        {/* Help Text */}
        <Typography
          sx={{
            fontSize: 13,
            color: '#94A3B8',
            fontFamily: 'Cairo, sans-serif',
            marginTop: 3,
          }}
        >
          {t('GLOBAL.needHelp') || 'Need help? Contact support.'}
        </Typography>
      </Box>

      {/* Footer */}
      <Typography
        sx={{
          position: 'absolute',
          bottom: 20,
          color: '#94A3B8',
          fontSize: 12,
          fontFamily: 'Cairo, sans-serif',
        }}
      >
        WenkGPS. All rights reserved 2026 ©
      </Typography>
    </Box>
  );
}
